import { type FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BsCaretDown, BsX } from 'react-icons/bs';
import { Draggable } from 'react-beautiful-dnd';

import { useDebouncedDispatch } from '@editor2/hooks/useDebouncedDispatch';
import { updateElement } from '@editor2/reducers/editor/editorSlice';
import { type RootState } from '@editor2/reducers/root';
import { getElementPreview } from '@editor2/services/elementService';
import { CollapseState } from '@editor2/types/collapseState';
import {
	type MCQuestionPool,
	type MCQuestion as MCQuestionType
} from '@editor2/types/pageElements';

import MCQuestion from './MCQuestion';
import { questionBlock } from './styles';
import { NewBadge } from '@editor2/components/Badge';
import { isNewQuestion } from '@editor2/services/changesStatusService';

interface Props {
	element: MCQuestionPool;
	question: MCQuestionType;
	index: number;
	courseId: number;
}

const PoolQuestion: FC<Props> = (props) => {
	const { question, index, element, courseId } = props;

	const [collapsed, setCollapsed] = useState(true);

	const { collapseState, initialCourse } = useSelector(
		(state: RootState) => state.courses[courseId]
	);

	useEffect(() => {
		switch (collapseState.elements) {
			case CollapseState.ExpandAllQuestions:
			case CollapseState.ExpandAll:
				setCollapsed(false);
				break;

			case CollapseState.CollapseAll:
				setCollapsed(true);
				break;

			default:
				break;
		}
	}, [collapseState.elements]);

	const dispatch = useDispatch();
	const debouncedDispatch = useDebouncedDispatch();
	const commonPayload = { elementId: element.family_id, courseId };

	const onChange = (value) => {
		dispatch(
			updateElement({
				...commonPayload,
				questions: [
					...element.questions.slice(0, index),
					{ ...question, ...value },
					...element.questions.slice(index + 1)
				]
			})
		);
	};
	const onDebouncedChange = (value) => {
		debouncedDispatch(
			updateElement({
				...commonPayload,
				questions: [
					...element.questions.slice(0, index),
					{ ...question, ...value },
					...element.questions.slice(index + 1)
				]
			})
		);
	};

	const handleDeleteQuestionClick = (event) => {
		event.stopPropagation();
		// eslint-disable-next-line no-alert
		if (window.confirm(`Are you sure to delete the question?`)) {
			dispatch(
				updateElement({
					...commonPayload,
					questions: [...element.questions.slice(0, index), ...element.questions.slice(index + 1)]
				})
			);
		}
	};

	return (
		<Draggable draggableId={question.family_id} index={index}>
			{(provided) => (
				<div ref={provided.innerRef} {...provided.draggableProps}>
					<div className={questionBlock}>
						<div
							{...provided.dragHandleProps}
							className={`header ${!collapsed && 'expanded'}`}
							onKeyPress={(e) => e.key === 'Enter' && setCollapsed(!collapsed)}
							onClick={() => setCollapsed(!collapsed)}>
							<div className="title">
								<div>Question</div>
								<div>
									{isNewQuestion(
										initialCourse.elements.entities[element.family_id] as MCQuestionPool,
										question
									) && <NewBadge />}
									<BsCaretDown className={collapsed ? 'collapseIconRotate' : 'collapseIcon'} />
									<BsX
										className="deleteQuestionIcon"
										onClick={(e) => handleDeleteQuestionClick(e)}
									/>
								</div>
							</div>
							{collapsed && <div className="preview">{getElementPreview(question)}</div>}
						</div>

						{!collapsed && (
							<div className="body">
								<MCQuestion
									element={question}
									onChange={onChange}
									onDebouncedChange={onDebouncedChange}
								/>
							</div>
						)}
					</div>
				</div>
			)}
		</Draggable>
	);
};

export default PoolQuestion;
